import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  ProductBreadcrumb,
  ProductHero,
  ProductGallery,
  QuickSpecsTabs,
  KeyFeatures,
  OptionalAccessories,
  Applications,
  WhyChooseLiftile,
  DownloadBrochure,
  PremiumFAQ,
  RelatedProducts,
  ProductFinalCTA,
} from "../components/product";
import {
  ArrowUp,
  Award,
  BadgeCheck,
  Boxes,
  Building2,
  Cable,
  ClipboardCheck,
  Factory,
  Flag,
  Frame,
  Hammer,
  HardHat,
  Home,
  Layers,
  Package,
  Paintbrush,
  Radio,
  Rocket,
  ShieldCheck,
  Siren,
  Sparkles,
  Truck,
  Warehouse,
  Wrench,
  Zap,
} from "lucide-react";

/* =====================================================================
   MONKEY LIFT (LML250) — dedicated product page.
   Composed entirely from /components/product/* shared primitives.
   Only product-specific data lives in this file.
   ===================================================================== */

const HERO_IMAGE = "/product-monkey-lift.jpg";

const HERO_SPECS = [
  { Icon: Package, value: "250 kg", label: "Net Lifting Load" },
  { Icon: ArrowUp, value: "Up to 40 m", label: "Maximum Height" },
  { Icon: Zap, value: "7–14 m/min", label: "Lifting Speed" },
  { Icon: Flag, value: "Made in India", label: "Engineered & Manufactured" },
];

const HERO_BADGES = [
  "Made in India",
  "Heavy Duty",
  "Owner Inspected",
  "Premium Components",
];

const GALLERY = [
  {
    src: "/product-monkey-lift.jpg",
    caption: "LIFTILE LML250 Monkey Lift — compact powerhouse for slab-level material transport",
  },
];

const QUICK_SPECS = [
  { label: "Net Load", value: "250 kg" },
  { label: "Maximum Height", value: "40 m" },
  { label: "Lifting Speed", value: "7–14 m/min" },
  { label: "Electric Motor", value: "3 HP" },
];

const MODELS = {
  LML250: {
    name: "LML250",
    tagline: "Compact 250 kg monkey lift for slab-level material transport",
    rows: [
      { label: "Model", value: "LML250" },
      { label: "Net Load", value: "250 kg" },
      { label: "Wire Rope", value: "8 mm" },
      { label: "Electric Motor", value: "3 HP" },
      { label: "Speed", value: "7–14 m/min" },
      { label: "Maximum Height", value: "40 m" },
      { label: "Power Supply", value: "Single Phase" },
      { label: "Country of Origin", value: "Made in India" },
    ],
  },
};

const FEATURES = [
  {
    Icon: Layers,
    title: "Heavy Duty Steel Structure",
    desc: "High-strength steel structure engineered for demanding site conditions and long service life.",
  },
  {
    Icon: Sparkles,
    title: "Premium Powder Coated Finish",
    desc: "7-tank pre-treatment with durable powder coating for corrosion resistance and a professional finish.",
  },
  {
    Icon: Frame,
    title: "Heavy Duty Fabricated Frame",
    desc: "Precision-welded fabricated frame delivers rigidity, stability and reliable performance under full load.",
  },
  {
    Icon: Award,
    title: "Premium Branded Components",
    desc: "Manufactured using trusted branded components including Usha Martin wire rope and Crompton motor.",
  },
  {
    Icon: Radio,
    title: "Wired Remote Control",
    desc: "Precise and responsive wired remote control enables safe operation from a convenient location.",
  },
  {
    Icon: Siren,
    title: "Emergency Stop Button",
    desc: "Dedicated emergency stop halts machine operation instantly during unsafe or unexpected conditions.",
  },
  {
    Icon: Cable,
    title: "Heavy Duty Wire Rope",
    desc: "ISI-certified 8 mm wire rope offers superior strength, durability and dependable lifting.",
  },
  {
    Icon: Wrench,
    title: "Low Maintenance Design",
    desc: "Simple mechanical design with easy access to service points keeps downtime and running costs low.",
  },
  {
    Icon: Rocket,
    title: "Easy Installation",
    desc: "Quick to install and dismantle — no permanent foundation required, ideal for fast-turnaround projects.",
  },
  {
    Icon: Boxes,
    title: "Compact Construction",
    desc: "Small footprint design fits tight construction sites, interior spaces and renovation projects.",
  },
  {
    Icon: ClipboardCheck,
    title: "Owner Inspected Before Dispatch",
    desc: "Every LML250 is personally inspected and quality signed off before it leaves the factory.",
  },
  {
    Icon: BadgeCheck,
    title: "Tested Before Dispatch",
    desc: "Full trial run and safety verification on every machine confirms load, brake and safety response.",
  },
];

const ACCESSORIES = [
  {
    Icon: Package,
    image: "/monkey-lift-accessory-concrete-bucket.png",
    title: "Concrete Bucket",
    desc: "Heavy-duty bucket for safe vertical concrete transport on small and mid-scale construction sites.",
  },
  {
    Icon: Boxes,
    image: "/monkey-lift-accessory-square-bucket.png",
    title: "Square Bucket",
    desc: "Rectangular material bucket for bricks, blocks, sand, aggregate and general construction materials.",
  },
  {
    Icon: Truck,
    image: "/wheel-barrow-lt1x.png",
    title: "Single Wheel Barrow",
    desc: "Compact single-wheel material transport barrow — ideal companion for tight interior sites.",
  },
  {
    Icon: Truck,
    image: "/wheel-barrow-lt2x.png",
    title: "Double Wheel Barrow",
    desc: "Heavy-duty twin-wheel barrow for larger material payloads and improved stability on rough ground.",
  },
  {
    Icon: Boxes,
    title: "Material Basket",
    desc: "Sturdy mesh basket attachment for lifting loose materials, tools and general construction supplies.",
  },
];

const APPLICATIONS = [
  {
    Icon: Home,
    title: "Residential Construction",
    desc: "Reliable slab-level material lifting for individual homes, villas and small residential projects.",
  },
  {
    Icon: Building2,
    title: "Commercial Buildings",
    desc: "Efficient material transport for offices, retail stores and small commercial developments.",
  },
  {
    Icon: Paintbrush,
    title: "Interior Construction",
    desc: "Compact form factor makes the LML250 ideal for interior finishing and fit-out projects.",
  },
  {
    Icon: Hammer,
    title: "Building Renovation",
    desc: "Fast to install, easy to relocate — perfect for renovation and refurbishment work.",
  },
  {
    Icon: Truck,
    title: "Material Transportation",
    desc: "Safe vertical transport of cement bags, tiles, bricks, tools and light finishing materials.",
  },
  {
    Icon: HardHat,
    title: "Small Construction Projects",
    desc: "Purpose-built for compact sites where a full tower hoist is oversized or impractical.",
  },
  {
    Icon: Warehouse,
    title: "Warehouse Material Handling",
    desc: "Dependable material lifting for warehouses, godowns and small-scale distribution centres.",
  },
  {
    Icon: Factory,
    title: "Industrial Sites",
    desc: "Trusted performance on industrial workshops, plants and light manufacturing environments.",
  },
];

const TRUST_CARDS = [
  {
    Icon: HardHat,
    title: "Heavy Duty Construction",
    desc: "Precision-welded steel structure built for demanding site conditions and long service life.",
  },
  {
    Icon: Award,
    title: "Premium Components",
    desc: "Trusted branded electricals, wire rope and hardware from leading Indian and global brands.",
  },
  {
    Icon: ClipboardCheck,
    title: "Owner Inspected",
    desc: "Every LML250 is personally inspected and quality signed off before dispatch.",
  },
  {
    Icon: Flag,
    title: "Made in India",
    desc: "Designed and manufactured in India for Indian construction site conditions.",
  },
  {
    Icon: Wrench,
    title: "Easy Maintenance",
    desc: "Simple mechanical design with easy access to service points keeps running costs low.",
  },
  {
    Icon: ShieldCheck,
    title: "Reliable Performance",
    desc: "Trial-run tested with safety verification for dependable performance project after project.",
  },
];

const FAQS = [
  {
    q: "What is the lifting capacity of the LIFTILE Monkey Lift?",
    a: "The LIFTILE LML250 Monkey Lift is engineered to safely lift up to 250 kg of construction materials to a maximum height of 40 metres.",
  },
  {
    q: "Is the Monkey Lift easy to install?",
    a: "Yes.\n\nThe LML250 is designed for quick, no-foundation installation. It can be assembled, commissioned and moved between site locations with minimal effort — ideal for renovation and interior finishing projects.",
  },
  {
    q: "What maintenance does the Monkey Lift require?",
    a: "The LML250 uses a simple, low-maintenance mechanical design. Routine inspection of the wire rope, brake, wired remote and electrical components — plus periodic lubrication of moving parts — is all that is typically required for reliable long-term operation.",
  },
  {
    q: "What safety features are included?",
    a: "The LML250 comes with a dedicated emergency stop button, heavy-duty ISI-certified wire rope, an owner-inspected build quality check and trial-run testing before dispatch. Every machine is verified for safe load, brake response and reliable operation before it leaves the factory.",
  },
  {
    q: "Does LIFTILE provide a warranty on the Monkey Lift?",
    a: "Yes.\n\nEvery LIFTILE Monkey Lift is backed by our standard manufacturer's warranty on structural components and electricals. Get in touch with our sales team for the current warranty terms applicable to your project and region.",
  },
  {
    q: "Is service and support available across India?",
    a: "Yes.\n\nLIFTILE provides installation guidance, spare parts, technical assistance and after-sales service across India through our nationwide support network.",
  },
];

const RELATED_PRODUCTS = [
  {
    slug: "conventional-tower-hoist",
    name: "Conventional Tower Hoist",
    desc: "LTC Series wire-rope tower hoist for dependable material lifting up to 65 m on residential and industrial projects.",
    image: "/conventional-tower-hoist-site-2.jpeg",
  },
  {
    slug: "multi-functional-tower-hoist",
    name: "Multi Functional Tower Hoist",
    desc: "LMTB Series flagship tower hoist with interchangeable buckets, rebar carrier and heights up to 125 m.",
    image: "/multi-functional-tower-hoist-catalog.png",
  },
  {
    slug: "wheel-barrow",
    name: "Wheel Barrow",
    desc: "Heavy-duty single and double wheel material transport barrows for smooth on-site material handling.",
    image: "/product-wheel-barrow.jpg",
  },
];

const CTA_BADGES = [
  "Made in India",
  "Heavy Duty",
  "Owner Inspected",
  "Premium Components",
];

/* ---------- Blueprint SVG for Section: Download Brochure ---------- */
const LML_BLUEPRINT_SVG = (
  <svg
    aria-hidden
    viewBox="0 0 400 400"
    className="absolute right-0 top-1/2 -translate-y-1/2 w-[280px] md:w-[360px] h-auto opacity-[0.14] pointer-events-none"
    fill="none"
    stroke="#FF6B1A"
    strokeWidth="1.2"
  >
    {/* Central compact mast */}
    <rect x="180" y="70" width="40" height="270" strokeDasharray="4 4" />
    <line x1="180" y1="120" x2="220" y2="120" />
    <line x1="180" y1="170" x2="220" y2="170" />
    <line x1="180" y1="220" x2="220" y2="220" />
    <line x1="180" y1="270" x2="220" y2="270" />
    {[
      [120, 170],
      [170, 220],
      [220, 270],
      [270, 320],
    ].map(([a, b]) => (
      <g key={a}>
        <line x1="180" y1={a} x2="220" y2={b} />
        <line x1="220" y1={a} x2="180" y2={b} />
      </g>
    ))}
    {/* Top pulley / hook assembly */}
    <circle cx="200" cy="55" r="18" />
    <line x1="200" y1="73" x2="200" y2="120" strokeDasharray="2 3" />
    {/* Suspended bucket */}
    <rect x="170" y="150" width="60" height="40" strokeDasharray="2 3" />
    {/* Base frame */}
    <rect x="140" y="340" width="120" height="24" />
    {/* Height dimension callout */}
    <line x1="80" y1="70" x2="80" y2="340" strokeDasharray="2 4" />
    <text
      x="55"
      y="205"
      fill="#FF6B1A"
      fontSize="10"
      fontFamily="Montserrat"
      opacity="0.6"
    >
      40m
    </text>
  </svg>
);

export default function MonkeyLiftPage() {
  return (
    <>
      <Navbar />
      <ProductBreadcrumb productName="Monkey Lift (LML250)" />

      {/* ─── SECTION 1: HERO ─── */}
      <ProductHero
        testId="lml-hero"
        backgroundImage={HERO_IMAGE}
        eyebrow="LML250 Series"
        title={
          <>
            LIFTILE
            <br />
            Monkey <span className="text-liftile-orange">Lift</span>
          </>
        }
        subtitle="Compact, reliable and efficient lifting solution for construction sites — designed for safe vertical transportation of construction materials with heavy-duty performance and easy installation."
        primaryCta={{ label: "Request a Quote", href: "/contact" }}
        secondaryCta={{ label: "Download Brochure" }}
        specs={HERO_SPECS}
        badges={HERO_BADGES}
      />

      {/* ─── SECTION 2: GALLERY ─── */}
      <ProductGallery
        testId="lml-gallery"
        heading={
          <>
            Monkey Lift{" "}
            <span className="text-liftile-orange">In Action</span>
          </>
        }
        subtitle="A closer look at the LIFTILE LML250 Monkey Lift — from precision fabrication at our factory to compact site applications across India."
        items={GALLERY}
        seriesLabel="LML250 Series"
        imageAlt="LIFTILE LML250 Monkey Lift"
      />

      {/* ─── SECTION 3: QUICK SPECS (split layout with 4 summary cards) ─── */}
      <QuickSpecsTabs
        testId="lml-quick-specs"
        heading={
          <>
            Quick <span className="text-liftile-orange">Specifications</span>
          </>
        }
        subtitle="Everything you need to know about the LML250 — compact, dependable and ready for site."
        quickSpecs={QUICK_SPECS}
        models={MODELS}
        defaultModel="LML250"
        footerNote={null}
      />

      {/* ─── SECTION 4: KEY FEATURES ─── */}
      <KeyFeatures
        testId="lml-features"
        heading={
          <>
            Built for Compact Sites.{" "}
            <br className="hidden sm:block" />
            <span className="text-liftile-orange">Engineered for Reliability.</span>
          </>
        }
        subtitle="Every LIFTILE LML250 Monkey Lift is engineered with premium branded components and rigorous quality inspections to deliver dependable performance on demanding construction sites."
        items={FEATURES}
      />

      {/* ─── SECTION 5: OPTIONAL ACCESSORIES ─── */}
      <OptionalAccessories
        testId="lml-accessories"
        heading={
          <>
            Optional <span className="text-liftile-orange">Accessories</span>
          </>
        }
        subtitle="Customize the LML250 for your specific project with our range of purpose-built lifting accessories."
        items={ACCESSORIES}
        gridCols="lg:grid-cols-3"
      />

      {/* ─── SECTION 6: APPLICATIONS ─── */}
      <Applications
        testId="lml-applications"
        heading={
          <>
            Ideal for Every{" "}
            <span className="text-liftile-orange">Compact Project</span>
          </>
        }
        subtitle="From individual homes to commercial fit-outs and industrial workshops — the LML250 is engineered for versatile everyday material lifting."
        items={APPLICATIONS}
        variant="dark"
        gridCols="lg:grid-cols-4"
      />

      {/* ─── SECTION 7: WHY CHOOSE LIFTILE ─── */}
      <WhyChooseLiftile
        testId="lml-why-choose"
        image="/product-monkey-lift.jpg"
        imageAlt="LIFTILE LML250 Monkey Lift — compact material lifting solution"
        imageCaption="LML250 Monkey Lift — compact, reliable and ready for site-day-one performance."
        floatingBadge={{ top: "Made in India", bottom: "Compact & Reliable" }}
        eyebrow="Why LML250"
        heading={
          <>
            Compact Power.{" "}
            <br className="hidden sm:block" />
            <span className="text-liftile-orange">Built to Last.</span>
          </>
        }
        subtitle="The LML250 combines heavy-duty construction, premium branded components and easy installation to deliver dependable material lifting on projects where a full tower hoist would be oversized."
        trustCards={TRUST_CARDS}
      />

      {/* ─── SECTION 8: DOWNLOAD BROCHURE ─── */}
      <DownloadBrochure
        testId="lml-brochure"
        heading={
          <>
            Download Product{" "}
            <span className="text-liftile-orange">Brochure</span>
          </>
        }
        subtitle="Download the complete LML250 technical catalogue including specifications, accessories, drawings and product information."
        blueprintSvg={LML_BLUEPRINT_SVG}
      />

      {/* ─── SECTION 9: FAQ ─── */}
      <PremiumFAQ
        testId="lml-faq"
        heading={
          <>
            Frequently Asked{" "}
            <span className="text-liftile-orange">Questions</span>
          </>
        }
        subtitle="Everything builders, contractors and site engineers most often ask us about the LIFTILE Monkey Lift."
        items={FAQS}
      />

      {/* ─── SECTION 10: RELATED PRODUCTS ─── */}
      <RelatedProducts
        testId="lml-related"
        heading={
          <>
            Explore More{" "}
            <span className="text-liftile-orange">LIFTILE Solutions</span>
          </>
        }
        items={RELATED_PRODUCTS}
      />

      {/* ─── SECTION 11: FINAL CTA ─── */}
      <ProductFinalCTA
        testId="lml-product-cta"
        backgroundImage="/project-thiruvananthapuram.png"
        backgroundAlt="LIFTILE Monkey Lift on a live construction site"
        heading={
          <>
            Lift Smarter.{" "}
            <span className="text-liftile-orange">Build Faster.</span>
          </>
        }
        subtitle="Discover the reliable performance of the LIFTILE Monkey Lift — designed for safe and efficient material lifting on construction sites."
        primaryCta={{ label: "Request a Quote", href: "/contact" }}
        secondaryCta={{ label: "Contact Sales", href: "/contact" }}
        trustBadges={CTA_BADGES}
      />

      <Footer />
    </>
  );
}
